import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2, Save, MapPin, Trees, Coffee, Droplets, Bus, Link2, Toilet, Car, ShoppingBag, Beer, Utensils, Flame, ShowerHead } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// Terracotta pin — matches the basecamp marker style used elsewhere so the
// operator immediately sees "this is where the basecamp will sit".
const pickerIcon = L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="td-marker" style="background:#D19C4C;color:#2C3A2E;">🪑</div>`,
});

/** Inner map child that captures single-clicks and reports lat/lng back up
 *  the tree. Kept as its own component because `useMapEvents` has to live
 *  inside the <MapContainer> subtree. */
function ClickToSetCoords({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

const LEAD_OPTIONS = [
  { v: "strictly_on_lead", label: "Strictly on lead" },
  { v: "off_lead_allowed", label: "Off-lead allowed" },
  { v: "fully_enclosed", label: "Fully enclosed" },
];
const ENERGY_OPTIONS = [
  { v: "chill", label: "Chill" },
  { v: "high_energy", label: "High energy" },
];
// Ordered so the operator can tick the most common ones first. Each entry maps
// directly onto a backend boolean on the basecamp document.
const AMENITY_FIELDS = [
  { k: "has_water_tap", label: "Water tap", Icon: Droplets },
  { k: "dog_waste_bins", label: "Dog waste bins", Icon: Trees },
  { k: "cafe_on_site", label: "Cafe on site", Icon: Coffee },
  { k: "has_toilets", label: "Toilets", Icon: Toilet },
  { k: "has_car_park", label: "Car park", Icon: Car },
  { k: "has_shop", label: "Shop", Icon: ShoppingBag },
  { k: "pub_nearby", label: "Pub nearby", Icon: Beer },
  { k: "picnic_tables", label: "Picnic tables", Icon: Utensils },
  { k: "bbq_allowed", label: "BBQ area", Icon: Flame },
  { k: "dog_wash_station", label: "Dog wash", Icon: ShowerHead },
];

export default function AdminAddBasecamp() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    google_maps_url: "",
    lat: "",
    lng: "",
    has_water_tap: false,
    dog_waste_bins: false,
    cafe_on_site: false,
    has_toilets: false,
    has_car_park: false,
    has_shop: false,
    pub_nearby: false,
    picnic_tables: false,
    bbq_allowed: false,
    dog_wash_station: false,
    lead_status: "strictly_on_lead",
    energy_match: "chill",
    bus_distance_meters: "",
    bus_path_quality: "",
    linked_trail_ids: "",
  });
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Live preview: when the operator tabs out of the Google Maps URL field,
  // call the same parser the backend uses at submit time and populate the
  // visible Lat / Lng inputs so they can verify before saving.
  const previewCoords = async (url) => {
    const trimmed = (url || "").trim();
    if (!trimmed) return;
    setParsing(true);
    try {
      const r = await api.post("/admin/basecamps/parse-url", { google_maps_url: trimmed });
      const lat = r.data?.lat;
      const lng = r.data?.lng;
      if (typeof lat === "number" && typeof lng === "number") {
        setForm((s) => ({ ...s, lat: String(lat), lng: String(lng) }));
        toast.success(`Coords extracted: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't extract coordinates from that URL.");
    } finally {
      setParsing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.google_maps_url.trim() && (!form.lat || !form.lng)) {
      return toast.error("Paste a Google Maps URL, or enter lat + lng manually.");
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      has_water_tap: form.has_water_tap,
      dog_waste_bins: form.dog_waste_bins,
      cafe_on_site: form.cafe_on_site,
      has_toilets: form.has_toilets,
      has_car_park: form.has_car_park,
      has_shop: form.has_shop,
      pub_nearby: form.pub_nearby,
      picnic_tables: form.picnic_tables,
      bbq_allowed: form.bbq_allowed,
      dog_wash_station: form.dog_wash_station,
      lead_status: form.lead_status,
      energy_match: form.energy_match,
      bus_distance_meters: form.bus_distance_meters ? parseInt(form.bus_distance_meters, 10) : null,
      bus_path_quality: form.bus_path_quality.trim(),
      linked_trail_ids: form.linked_trail_ids
        .split(",").map((s) => s.trim()).filter(Boolean),
      lat: form.lat ? parseFloat(form.lat) : 0,   // placeholders — backend will resolve from URL if provided
      lng: form.lng ? parseFloat(form.lng) : 0,
    };
    if (form.google_maps_url.trim()) payload.google_maps_url = form.google_maps_url.trim();
    // Only send explicit lat/lng when we have real values — otherwise let the URL parser win.
    if (!form.lat || !form.lng) { delete payload.lat; delete payload.lng; }

    setSaving(true);
    try {
      const r = await api.post("/admin/basecamps", payload);
      toast.success(`Basecamp created — ${r.data.name}`);
      navigate(`/basecamp/${r.data.basecamp_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create basecamp.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-add-basecamp">
      <header className="mb-5">
        <div className="td-section-label">Admin</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text mt-1 flex items-center gap-2">
          <Trees size={28} className="text-brand-primary" /> Add basecamp
        </h1>
        <p className="text-brand-subtle text-sm mt-1">
          Dog-friendly day spots, picnic sites and meadows — the places walkers pause between trails.
        </p>
      </header>

      <form onSubmit={submit} className="td-card p-5 sm:p-6 space-y-4">
        <div>
          <label className="td-section-label">Name</label>
          <input
            data-testid="bc-name"
            className="td-input mt-1"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Bute Park Picnic Meadow"
            required
          />
        </div>

        <div>
          <label className="td-section-label">Description</label>
          <textarea
            data-testid="bc-description"
            rows={3}
            className="td-input mt-1 resize-none"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="A one-paragraph feel for the spot."
          />
        </div>

        <div>
          <label className="td-section-label flex items-center gap-1">
            <MapPin size={11} /> Google Maps URL <span className="text-brand-subtle font-normal">(preferred)</span>
          </label>
          <input
            data-testid="bc-gmaps-url"
            className="td-input mt-1"
            value={form.google_maps_url}
            onChange={(e) => set("google_maps_url", e.target.value)}
            onBlur={(e) => previewCoords(e.target.value)}
            placeholder="Paste any google.com/maps or maps.app.goo.gl link"
          />
          <p className="text-[11px] text-brand-subtle mt-1">
            {parsing ? (
              <span className="inline-flex items-center gap-1 text-brand-primary">
                <Loader2 size={11} className="animate-spin" /> Extracting coordinates…
              </span>
            ) : (
              <>We&apos;ll extract the coordinates automatically when you tab out — same parser as the Campsite form.</>
            )}
          </p>
        </div>

        {/* Interactive picker map — click anywhere to auto-fill lat/lng.
            Defaults to Newport, Wales so the operator lands in a familiar
            starting frame; they can pan/zoom before clicking. */}
        <div>
          <label className="td-section-label flex items-center gap-1">
            <MapPin size={11} /> Pick on the map
            <span className="text-brand-subtle font-normal">(click to drop the basecamp)</span>
          </label>
          <div
            data-testid="bc-map-picker"
            className="td-card overflow-hidden h-56 mt-1"
          >
            <MapContainer
              center={[51.58, -2.99]}
              zoom={13}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              <ClickToSetCoords
                onPick={(lat, lng) => {
                  // Stringified so the number inputs stay controlled + a
                  // reasonable precision (~1 m at UK latitudes) is enough.
                  setForm((s) => ({
                    ...s,
                    lat: lat.toFixed(6),
                    lng: lng.toFixed(6),
                  }));
                }}
              />
              {form.lat && form.lng && !isNaN(parseFloat(form.lat)) && !isNaN(parseFloat(form.lng)) && (
                <Marker
                  position={[parseFloat(form.lat), parseFloat(form.lng)]}
                  icon={pickerIcon}
                />
              )}
            </MapContainer>
          </div>
          <p className="text-[11px] text-brand-subtle mt-1">
            Tip: paste a Google Maps URL above for a precise drop, or click the map here for a quick pin.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="td-section-label">Lat <span className="text-brand-subtle font-normal">(fallback)</span></label>
            <input
              data-testid="bc-lat"
              type="number" step="any"
              className="td-input mt-1"
              value={form.lat}
              onChange={(e) => set("lat", e.target.value)}
              placeholder="e.g. 51.4874"
            />
          </div>
          <div>
            <label className="td-section-label">Lng</label>
            <input
              data-testid="bc-lng"
              type="number" step="any"
              className="td-input mt-1"
              value={form.lng}
              onChange={(e) => set("lng", e.target.value)}
              placeholder="e.g. -3.1836"
            />
          </div>
        </div>

        <fieldset className="pt-2">
          <legend className="td-section-label">Amenities</legend>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {AMENITY_FIELDS.map(({ k, label, Icon }) => (
              <label
                key={k}
                data-testid={`bc-${k}-wrap`}
                className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                  form[k] ? "bg-brand-primary/10 border-brand-primary/40" : "bg-brand-sand/50 border-brand-border"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`bc-${k}`}
                  checked={form[k]}
                  onChange={(e) => set(k, e.target.checked)}
                  className="accent-brand-primary"
                />
                <Icon size={13} className="text-brand-primary" />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <fieldset>
            <legend className="td-section-label">Lead status</legend>
            <select
              data-testid="bc-lead-status"
              className="td-input mt-1"
              value={form.lead_status}
              onChange={(e) => set("lead_status", e.target.value)}
            >
              {LEAD_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </fieldset>
          <fieldset>
            <legend className="td-section-label">Energy match</legend>
            <select
              data-testid="bc-energy"
              className="td-input mt-1"
              value={form.energy_match}
              onChange={(e) => set("energy_match", e.target.value)}
            >
              {ENERGY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </fieldset>
        </div>

        <fieldset className="pt-2">
          <legend className="td-section-label flex items-center gap-1"><Bus size={11} /> Transit last-mile</legend>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <input
              data-testid="bc-bus-dist"
              type="number" min="0" step="1"
              className="td-input"
              value={form.bus_distance_meters}
              onChange={(e) => set("bus_distance_meters", e.target.value)}
              placeholder="Bus stop distance (m)"
            />
            <input
              data-testid="bc-bus-path"
              className="td-input"
              value={form.bus_path_quality}
              onChange={(e) => set("bus_path_quality", e.target.value)}
              placeholder="Path quality e.g. Paved path"
            />
          </div>
        </fieldset>

        <div>
          <label className="td-section-label flex items-center gap-1"><Link2 size={11} /> Linked trail IDs</label>
          <input
            data-testid="bc-linked"
            className="td-input mt-1"
            value={form.linked_trail_ids}
            onChange={(e) => set("linked_trail_ids", e.target.value)}
            placeholder="Comma-separated location_id values (e.g. loc_20026574caf3, loc_8bf650a82c41)"
          />
          <p className="text-[11px] text-brand-subtle mt-1">
            These become the &ldquo;Trails near here&rdquo; carousel on the basecamp detail page.
          </p>
        </div>

        <div className="pt-2 flex items-center gap-3">
          <button
            type="submit"
            data-testid="bc-submit"
            disabled={saving}
            className="td-btn-primary !py-2.5 !px-5"
          >
            {saving ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : (<><Save size={14} /> Save basecamp</>)}
          </button>
          <Link to="/admin/locations" className="text-sm text-brand-subtle hover:underline">Back to admin</Link>
        </div>
      </form>
    </div>
  );
}
